import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import api from './api';
import {
  clearWindowNameSession,
  readStorageJSON,
  readStorageValue,
  readWindowNameSession,
  removeStorageValue,
  writeStorageJSON,
  writeStorageValue,
  writeWindowNameSession,
} from './utils/storage';

const isGoogleTranslateProxyHost = (hostname = '') =>
  typeof hostname === 'string' &&
  (hostname.endsWith('.translate.goog') || hostname === 'translate.googleusercontent.com');

const buildGoogleTranslateLoginPath = (search = '') => {
  if (!search) {
    return '/';
  }

  const params = new URLSearchParams(search);
  const translatorParams = new URLSearchParams();

  params.forEach((value, key) => {
    if (key.startsWith('_x_tr_')) {
      translatorParams.set(key, value);
    }
  });

  const query = translatorParams.toString();
  return query ? `/?${query}` : '/';
};

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const initialWindowSession = readWindowNameSession();
  const [user, setUser] = useState(() => readStorageJSON('user') ?? initialWindowSession?.user ?? null);
  const [token, setToken] = useState(() => readStorageValue('token') ?? initialWindowSession?.token ?? null);
  const refreshPromiseRef = useRef(null);

  const redirectToLogin = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const loginPaths = ['/', '/login'];
    const isAlreadyOnLogin = loginPaths.includes(window.location.pathname);

    if (isAlreadyOnLogin) {
      return;
    }

    const { hostname, search } = window.location;
    if (isGoogleTranslateProxyHost(hostname)) {
      const redirectPath = buildGoogleTranslateLoginPath(search);
      window.location.replace(redirectPath);
      return;
    }

    window.location.replace('/');
  }, []);

  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      api.defaults.headers.common['x-authorization'] = token;
    } else {
      delete api.defaults.headers.common['Authorization'];
      delete api.defaults.headers.common['x-authorization'];
    }

    if (typeof window !== 'undefined') {
      if (token) {
        window.__APP_AUTH_TOKEN__ = token;
      } else {
        delete window.__APP_AUTH_TOKEN__;
      }
    }
  }, [token]);

  useEffect(() => {
    if (token || user) {
      writeWindowNameSession({ token, user });
      return;
    }

    clearWindowNameSession();
  }, [token, user]);

  const login = async (email, password) => {
    try {
      const res = await api.post('/auth/login', { email, password });
      setUser(res.data.user);
      setToken(res.data.token);
      writeStorageJSON('user', res.data.user);
      writeStorageValue('token', res.data.token);
      writeWindowNameSession({ user: res.data.user, token: res.data.token });
    } catch (err) {
      const msg = err.response?.data?.error || 'Falha no login';
      throw new Error(msg);
    }
  };

  const clearSession = useCallback(() => {
    setUser(null);
    setToken(null);
    removeStorageValue('user');
    removeStorageValue('token');
    clearWindowNameSession();
  }, []);

  const refreshSession = useCallback(() => {
    if (!token) {
      return Promise.reject(new Error('Sessão inválida'));
    }

    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    const request = api
      .post(
        '/auth/refresh',
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'x-authorization': token,
          },
          skipAuthRefresh: true,
        }
      )
      .then((res) => {
        const nextToken = res?.data?.token;
        const nextUser = res?.data?.user ?? null;
        if (!nextToken) {
          throw new Error('Token inválido');
        }

        setToken(nextToken);
        writeStorageValue('token', nextToken);
        writeWindowNameSession({ user: nextUser ?? user ?? null, token: nextToken });

        if (nextUser) {
          setUser(nextUser);
          writeStorageJSON('user', nextUser);
        } else if (user) {
          writeStorageJSON('user', user);
        }

        return nextToken;
      })
      .finally(() => {
        refreshPromiseRef.current = null;
      });

    refreshPromiseRef.current = request;
    return request;
  }, [token, user]);

  const logout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      console.error('Falha ao registrar logout', err);
    } finally {
      clearSession();
    }
  };

  const updateUser = (u) => {
    setUser(u);
    writeStorageJSON('user', u);
    writeWindowNameSession({ user: u, token });
  };

  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;

        if (status === 401 && token) {
          const originalRequest = error.config ?? null;
          const requestUrl = typeof originalRequest?.url === 'string' ? originalRequest.url : '';
          const normalizedUrl = (() => {
            if (!requestUrl) return '';
            if (/^https?:/i.test(requestUrl)) {
              try {
                const parsed = new URL(requestUrl);
                return parsed.pathname || '';
              } catch (err) {
                return requestUrl;
              }
            }
            return requestUrl;
          })();

          const skipRefresh = Boolean(
            originalRequest?.skipAuthRefresh ||
              originalRequest?.__isRetryRequest ||
              /\/auth\/refresh/i.test(normalizedUrl) ||
              /\/auth\/login/i.test(normalizedUrl)
          );

          if (!skipRefresh) {
            try {
              const nextToken = await refreshSession();
              if (nextToken) {
                if (originalRequest) {
                  const ensureHeaders = () => {
                    if (!originalRequest.headers) {
                      originalRequest.headers = {};
                    }
                    return originalRequest.headers;
                  };
                  const headers = ensureHeaders();
                  const setHeader = (name, value) => {
                    if (!headers) return;
                    if (typeof headers.set === 'function') {
                      headers.set(name, value);
                    } else {
                      headers[name] = value;
                    }
                  };
                  setHeader('Authorization', `Bearer ${nextToken}`);
                  setHeader('x-authorization', nextToken);
                  originalRequest.__isRetryRequest = true;
                  return api(originalRequest);
                }
                return Promise.reject(error);
              }
            } catch (refreshError) {
              // Se a renovação falhar, seguimos com o fluxo de limpeza
            }
          }

          clearSession();
          redirectToLogin();
        }

        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptor);
    };
  }, [clearSession, redirectToLogin, refreshSession, token]);

  return (
    <AuthContext.Provider value={{ user, token, login, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
